/**
 * The team -> division index.
 *
 * `useUserRegistrationTeams` cannot help here: it is fetched FROM a division id
 * plus a role, so it cannot run before a division exists. The registration
 * payload's top-level `teams[]` can: each team carries the division it belongs
 * to, and a team belongs to exactly one, so a team resolves a division with no
 * extra request.
 */

import { divisionSelectableForRole } from './divisionRole'
import { privateTeamKey } from './revealedPrivateTeams'
import { teamIdentity, teamMatchesId } from './teamDisplay'
import type { DivisionTeam, StartDivision, StartRole, StartTeam } from './types'

/** A team from the top-level `teams[]`, carrying the division it belongs to. */
interface TeamWithDivision<T extends StartDivision> extends StartTeam {
  division: T
}

/**
 * Build the index from the top-level `teams[]`.
 *
 * Each team already carries its division, so there is nothing to flatten.
 */
export const teamsFromRegistration = <T extends StartDivision>(
  teams: TeamWithDivision<T>[] | null | undefined,
  role?: StartRole | null
): DivisionTeam<T>[] => {
  const index: DivisionTeam<T>[] = []

  for (const entry of teams ?? []) {
    if (!teamIdentity(entry) || !entry.division) {
      continue
    }
    // A team in a division that cannot accept this reader is a dead end.
    if (role && !divisionSelectableForRole(entry.division, role)) {
      continue
    }
    const { division, ...team } = entry
    index.push({ team, division })
  }

  return index
}

/**
 * Find one team by id, with its division.
 *
 * Used to resolve `?team_id=` into both a team and the division that owns it.
 * Unfiltered by role on purpose: the caller needs to tell "no such team" apart
 * from "that team is not open to this role", and can only do that if the lookup
 * itself does not hide the second case.
 *
 * Matches `uid` or `id` via `teamMatchesId`, the same pair the register step's
 * resolver accepts — the two run against different lists but read the same
 * link, so a team findable by one must be findable by the other.
 */
export const findDivisionTeamById = <T extends StartDivision>(
  teams: TeamWithDivision<T>[] | null | undefined,
  teamId: string | null | undefined
): DivisionTeam<T> | null => {
  if (!teamId) {
    return null
  }
  for (const entry of teams ?? []) {
    if (entry.division && teamMatchesId(entry, teamId)) {
      const { division, ...team } = entry
      return { team, division }
    }
  }
  return null
}

/**
 * Hide UNLISTED teams from the browsable list, keeping the one the reader is on.
 *
 * SN-4709 follow-up (Inon + Pouya, Sep 17). "Private" means unlisted, not
 * restricted: a private team is joinable by anyone holding
 * `/registration/{root}?team_id={team uid}`, and the server therefore does NOT
 * filter the top-level `teams[]` — that list is exactly what the link resolves
 * against. Filtering it server-side would break the link it exists to serve, so
 * the hiding happens here instead, where the exception can be made.
 *
 * THE EXCEPTION IS THE WHOLE POINT. The selected team's own row survives, so a
 * reader who arrived by link, or restored one from their session, still sees the
 * team they are registering for — highlighted, in a list it is otherwise absent
 * from. Matched on IDENTITY plus DIVISION: one team can hold registrations in
 * several divisions, and only the row the reader actually selected may come
 * back. The others stay hidden, exactly as if they belonged to another team.
 *
 * `revealedKeys` widens that exception over TIME but not in scope: a row shown
 * once in this session keeps its place in the list after the selection moves
 * off it, so a reader can try another team and still come back to their own.
 * Each key is the same team-plus-division pair. See `revealedPrivateTeams`.
 *
 * Deliberately applied to the rendered index only. The pre-select, the session
 * restore and `sessionTeamToRestore` all read the RAW `registration.teams`, so
 * none of them can be defeated by this filter — a private team must stay
 * resolvable even while it is invisible.
 */
export const visibleStartTeams = <T extends StartDivision>(
  index: DivisionTeam<T>[],
  selected: DivisionTeam<T> | null | undefined,
  revealedKeys?: ReadonlySet<string>
): DivisionTeam<T>[] => {
  const selectedId = teamIdentity(selected?.team)
  const selectedDivisionId = selected?.division?.id

  return index.filter((entry) => {
    if (!entry.team?.is_private) {
      return true
    }
    if (
      selectedId &&
      selectedDivisionId &&
      teamMatchesId(entry.team, selectedId) &&
      entry.division?.id === selectedDivisionId
    ) {
      return true
    }
    // Already shown earlier in this session — see `revealedPrivateTeams` for why
    // a row the reader could select a moment ago stays selectable. Keyed the
    // same way as the selection exception, so it is no wider: revealing one
    // division's row says nothing about the same team's rows elsewhere.
    const entryKey = privateTeamKey(
      teamIdentity(entry.team),
      entry.division?.id
    )
    return Boolean(entryKey && revealedKeys?.has(entryKey))
  })
}
